'use client';

import { useQueries } from '@tanstack/react-query';
import { LeadEventRosterMode, TeamsApi } from '@up2stay/up2promo-api-client';
import type { TeamWithCountsDto } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@up2stay/up2promo-ui';
import { Search, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

import { resolveRosterPreview } from '@/core/components/leads/lead-event-form-utils';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { teamsQueryKeys } from '@/core/query-keys/workspace-members-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type LeadEventRosterMember = {
  userId?: string;
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
};

export function leadEventMemberLabel(m: LeadEventRosterMember): string {
  const u = m.user;
  if (!u) return m.userId ?? '';
  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return name || u.email || m.userId || '';
}

export type LeadEventRosterFieldsLabels = {
  rosterMode: string;
  rosterEveryone: string;
  rosterEveryoneHint: string;
  rosterSelected: string;
  rosterSelectedHint: string;
  selectUsers: string;
  selectTeams: string;
  searchMembers: string;
  searchTeams: string;
  noMembersMatch: string;
  noTeamsMatch: string;
  resolvedCount: string;
  /** Singular form for exactly one person. */
  resolvedCountOne: string;
  rosterEmptyHint: string;
};

type LeadEventRosterFieldsProps = {
  rosterMode: LeadEventRosterMode;
  onRosterModeChange: (mode: LeadEventRosterMode) => void;
  selectedUserIds: string[];
  onSelectedUserIdsChange: (ids: string[]) => void;
  selectedTeamIds: string[];
  onSelectedTeamIdsChange: (ids: string[]) => void;
  members: LeadEventRosterMember[];
  teams: TeamWithCountsDto[];
  labels: LeadEventRosterFieldsLabels;
  locale: string;
};

function toggleId(
  id: string,
  list: string[],
  setList: (next: string[]) => void,
) {
  setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
}

function RosterModeOption({
  value,
  id,
  title,
  description,
}: {
  value: string;
  id: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-1 rounded-lg border border-border/80 bg-card pr-1 transition-colors has-[[data-state=checked]]:border-foreground/35 has-[[data-state=checked]]:bg-accent/40">
      <Label
        htmlFor={id}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 px-3 py-2.5"
      >
        <RadioGroupItem value={value} id={id} className="shrink-0" />
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
          {title}
        </span>
      </Label>
      <SettingsInfoHint label={title}>{description}</SettingsInfoHint>
    </div>
  );
}

export function LeadEventRosterFields({
  rosterMode,
  onRosterModeChange,
  selectedUserIds,
  onSelectedUserIdsChange,
  selectedTeamIds,
  onSelectedTeamIdsChange,
  members,
  teams,
  labels,
  locale,
}: LeadEventRosterFieldsProps) {
  const [memberSearch, setMemberSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');

  const collator = locale === 'de' ? 'de' : 'en';

  const workspaceId = useCurrentWorkspace()?.id;
  const selectedTeams = useMemo(
    () => teams.filter((team) => selectedTeamIds.includes(team.id)),
    [teams, selectedTeamIds],
  );
  // Member ids of the picked teams, so overlaps count once (DEV-820).
  const teamMemberQueries = useQueries({
    queries: selectedTeams.map((team) => ({
      queryKey: teamsQueryKeys.members(workspaceId ?? '', team.id),
      queryFn: async () => {
        const res = await TeamsApi.listMembers({
          path: { workspaceId: workspaceId!, teamId: team.id },
        });
        return (res.data ?? []).map((member) => member.userId);
      },
      enabled: !!workspaceId && rosterMode === LeadEventRosterMode.SELECTED,
      staleTime: 60_000,
    })),
  });
  const teamMemberIds = teamMemberQueries.map((query) => query.data);

  const resolvedPreview = useMemo(() => {
    if (rosterMode === LeadEventRosterMode.EVERYONE) {
      return { total: members.length, direct: members.length, viaTeams: 0 };
    }
    return resolveRosterPreview(
      selectedUserIds,
      selectedTeams.map((team, index) => ({
        memberCount: team.memberCount,
        memberUserIds: teamMemberIds[index],
      })),
    );
  }, [
    rosterMode,
    selectedUserIds,
    selectedTeams,
    members.length,
    teamMemberIds,
  ]);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    const rows = members
      .map((m) => {
        const uid = m.userId?.trim();
        if (!uid) return null;
        const label = leadEventMemberLabel(m);
        return { uid, label };
      })
      .filter((row): row is { uid: string; label: string } => row !== null);

    rows.sort((a, b) =>
      a.label.localeCompare(b.label, collator, { sensitivity: 'base' }),
    );

    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.label.toLowerCase().includes(q) ||
        row.uid.toLowerCase().includes(q),
    );
  }, [members, memberSearch, collator]);

  const filteredTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    const rows = [...teams].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', collator, {
        sensitivity: 'base',
      }),
    );
    if (!q) return rows;
    return rows.filter((t) => (t.name ?? '').toLowerCase().includes(q));
  }, [teams, teamSearch, collator]);

  const resolvedCountLabel = (
    resolvedPreview.total === 1 ? labels.resolvedCountOne : labels.resolvedCount
  )
    .replace('{total}', String(resolvedPreview.total))
    .replace('{direct}', String(resolvedPreview.direct))
    .replace('{viaTeams}', String(resolvedPreview.viaTeams));

  return (
    <div className="space-y-3">
      <RadioGroup
        value={rosterMode}
        onValueChange={(value) =>
          onRosterModeChange(value as LeadEventRosterMode)
        }
        className="flex flex-col gap-2"
        aria-label={labels.rosterMode}
      >
        <RosterModeOption
          value={LeadEventRosterMode.EVERYONE}
          id="roster-everyone"
          title={labels.rosterEveryone}
          description={labels.rosterEveryoneHint}
        />
        <RosterModeOption
          value={LeadEventRosterMode.SELECTED}
          id="roster-selected"
          title={labels.rosterSelected}
          description={labels.rosterSelectedHint}
        />
      </RadioGroup>

      {rosterMode === LeadEventRosterMode.SELECTED ? (
        <div className="space-y-4 rounded-lg border border-border/80 bg-muted/20 p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="gap-1 font-normal">
              <Users className="h-3 w-3" aria-hidden />
              {resolvedCountLabel}
            </Badge>
            {resolvedPreview.total === 0 ? (
              <p className="text-xs text-destructive">
                {labels.rosterEmptyHint}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{labels.selectUsers}</p>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder={labels.searchMembers}
                className="h-9 pl-9"
                aria-label={labels.searchMembers}
              />
            </div>
            <div className="max-h-36 space-y-0.5 overflow-y-auto rounded-md border border-border/60 bg-card p-1">
              {filteredMembers.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {labels.noMembersMatch}
                </p>
              ) : (
                filteredMembers.map(({ uid, label }) => (
                  <label
                    key={uid}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent/50"
                  >
                    <Checkbox
                      checked={selectedUserIds.includes(uid)}
                      onCheckedChange={() =>
                        toggleId(uid, selectedUserIds, onSelectedUserIdsChange)
                      }
                    />
                    <span className="min-w-0 truncate">{label}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{labels.selectTeams}</p>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                placeholder={labels.searchTeams}
                className="h-9 pl-9"
                aria-label={labels.searchTeams}
              />
            </div>
            <div className="max-h-36 space-y-0.5 overflow-y-auto rounded-md border border-border/60 bg-card p-1">
              {filteredTeams.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {labels.noTeamsMatch}
                </p>
              ) : (
                filteredTeams.map((t) => (
                  <label
                    key={t.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent/50"
                  >
                    <Checkbox
                      checked={selectedTeamIds.includes(t.id)}
                      onCheckedChange={() =>
                        toggleId(t.id, selectedTeamIds, onSelectedTeamIdsChange)
                      }
                    />
                    <span className="min-w-0 truncate">
                      {t.name}
                      {typeof t.memberCount === 'number'
                        ? ` (${t.memberCount})`
                        : ''}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
